import React from "react";
import { graphql } from "react-relay";

import { faSpinnerThird } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Popover from "@radix-ui/react-popover";
import Markdown from "markdown-to-jsx";

import { commitMutationPromise } from "~/lib/relayUtil";
import { HIGHLIGHTS_MARKDOWN_OPTIONS } from "~/meetingRecorder/HighlightsPanel";
import { parseHumanReadableTimestamp, useIsSmallViewport } from "~/meetingRecorder/utils";
import { environment } from "~/models";
import { Modal } from "~/ui/legacyFellowIndex";

import type { ContextPopoverGenerateSummaryItemContextMutation } from "~/__generated__/ContextPopoverGenerateSummaryItemContextMutation.graphql";

import chaptersStyles from "./css/ChaptersPanel.module.css";
import styles from "./css/ContextPopover.module.css";

const generateSummaryItemContextMutation = graphql`
	mutation ContextPopoverGenerateSummaryItemContextMutation(
		$meetingRecordingId: ID!
		$itemType: SummaryItemType!
		$index: String!
	) {
		generateSummaryItemContext(meetingRecordingId: $meetingRecordingId, itemType: $itemType, index: $index) {
			context
		}
	}
`;

interface ContextPopoverProps {
	context: string;
	onTimestampClick: (timestamp: number) => void;
	children: React.ReactNode;
	itemText?: string;
	recordingId?: string;
	chapterRowId?: string;
	timestamp?: string;
	itemType?: "bullet" | "decision" | "action_item";
	lookupKey?: [number, number] | number; // [chapterIndex, bulletIndex] or decisionIndex
}

export const ContextPopover = ({
	context,
	onTimestampClick,
	children,
	itemText,
	recordingId,
	lookupKey,
	chapterRowId,
	timestamp,
	itemType,
}: ContextPopoverProps) => {
	const [loadingContext, setLoadingContext] = React.useState(false);
	const [fetchedContext, setFetchedContext] = React.useState<string | null>(null);
	const [open, setOpen] = React.useState(false);
	const isSmallViewport = useIsSmallViewport();
	const highlightedRowRef = React.useRef<HTMLElement | null>(null);

	// Only consider having context if the passed context has content
	const hasContext = Boolean(context?.trim());
	const shouldFetchContext = !hasContext && recordingId && lookupKey != undefined;

	const fetchContext = React.useCallback(async () => {
		if (!shouldFetchContext || fetchedContext !== null) return;

		try {
			setLoadingContext(true);
			// This mutation now uses WebSockets because it's added to WS_MUTATIONS in models/index.ts
			const result = await commitMutationPromise<ContextPopoverGenerateSummaryItemContextMutation>(environment, {
				mutation: generateSummaryItemContextMutation,
				variables: {
					meetingRecordingId: recordingId,
					itemType: itemType || "bullet",
					index: typeof lookupKey === "number" ? lookupKey.toString() : `${lookupKey[0]},${lookupKey[1]}`,
				},
			});

			if (result?.generateSummaryItemContext?.context) {
				setFetchedContext(result.generateSummaryItemContext.context);
			}
		} catch (error) {
			console.error("Failed to fetch context:", error);
		} finally {
			setLoadingContext(false);
		}
	}, [recordingId, lookupKey, shouldFetchContext, fetchedContext, itemType]);

	React.useEffect(() => {
		if (open && shouldFetchContext) {
			fetchContext();
		}
	}, [open, shouldFetchContext, fetchContext]);

	const contextToShow = fetchedContext || context;

	// Handle highlighting and opening for both mobile and desktop
	const handleOpenChange = React.useCallback(
		(isOpen: boolean) => {
			if (isOpen) {
				// Apply highlight when opening
				if (chapterRowId) {
					const bulletRow = document.getElementById(chapterRowId);
					if (bulletRow) {
						// Save reference to the bullet row
						highlightedRowRef.current = bulletRow as HTMLElement;
						// Add highlight class
						bulletRow.parentElement?.classList.add(chaptersStyles.chapterRowHighlighted);

						if (isSmallViewport) {
							bulletRow.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
							// Small delay for mobile to ensure the scroll completes
							setTimeout(() => {
								setOpen(true);
							}, 200);
							return; // Don't set open immediately for mobile
						}
					}
				}
			} else {
				// Remove highlight when closing
				// eslint-disable-next-line no-lonely-if
				if (highlightedRowRef.current) {
					highlightedRowRef.current.parentElement?.classList.remove(chaptersStyles.chapterRowHighlighted);
					highlightedRowRef.current = null;
				}
			}
			setOpen(isOpen);
		},
		[chapterRowId, isSmallViewport],
	);

	// Create a memoized close handler
	const handleClose = React.useCallback(() => {
		handleOpenChange(false);
	}, [handleOpenChange]);

	// Create a memoized click handler
	const handleClick = React.useCallback(async () => {
		handleOpenChange(true);
	}, [handleOpenChange]);
	const timestampClickCallback = React.useCallback(
		(timestamp: number) => {
			onTimestampClick(timestamp);
			handleClose();
		},
		[handleClose, onTimestampClick],
	);
	const handleItemTimestampClick = React.useCallback(
		(e: React.MouseEvent<HTMLSpanElement>) => {
			e.stopPropagation();
			if (timestamp) {
				const ts = parseHumanReadableTimestamp(timestamp);
				onTimestampClick(ts);
				handleClose();
			}
		},
		[handleClose, onTimestampClick, timestamp],
	);

	// Update useEffect to clean up highlight if component unmounts while modal is open
	React.useEffect(() => {
		return () => {
			if (highlightedRowRef.current) {
				highlightedRowRef.current.parentElement?.classList.remove(chaptersStyles.chapterRowHighlighted);
			}
		};
	}, []);
	const processedContext = React.useMemo(() => {
		if (loadingContext) {
			return [
				<div key="loading" className={styles.loadingContext}>
					<FontAwesomeIcon icon={faSpinnerThird} spin />
				</div>,
			];
		}

		if (!contextToShow?.trim() && !loadingContext && open && shouldFetchContext) {
			return [<p key="no-context">No additional context available.</p>];
		}

		// Replace weird quote characters and apostrophes with normal variants (e.g. “ -> " and ’ -> ')
		const processedContext = contextToShow?.replace(/[“”’]/g, '"').replace(/[‘’]/g, "'");

		// Split into paragraphs first
		const paragraphs = processedContext?.split(/\n+/) || [];

		return paragraphs.map((paragraph, pIndex) => {
			// Check if this is a blockquote
			if (paragraph.startsWith(">")) {
				let quoteContent = paragraph.slice(1).trim();

				if (quoteContent.length === 0) {
					return null;
				}

				// If the quote starts or ends with quote marks, remove them
				if (quoteContent.startsWith('"') && quoteContent.endsWith('"')) {
					quoteContent = quoteContent.slice(1, -1);
				}
				// eslint-disable-next-line react/no-array-index-key
				return <blockquote key={`p-${pIndex}`}>{processText(quoteContent, pIndex, timestampClickCallback)}</blockquote>;
			}

			// eslint-disable-next-line react/no-array-index-key
			return <p key={`p-${pIndex}`}>{processText(paragraph, pIndex, timestampClickCallback)}</p>;
		});
	}, [contextToShow, loadingContext, open, shouldFetchContext, timestampClickCallback]);
	if (isSmallViewport) {
		return (
			<>
				<div onClick={handleClick} style={{ display: "inline-block", cursor: "pointer" }}>
					{children}
				</div>
				{open && (
					<Modal onClose={handleClose} isOpen={open} className={styles.dialogContent}>
						<Modal.Header onClose={handleClose}>
							<Modal.Title>More context</Modal.Title>
						</Modal.Header>
						<Modal.Body className={styles.contextContent}>
							{itemText && (
								<div className={styles.itemHeader}>
									<div>
										<Markdown options={HIGHLIGHTS_MARKDOWN_OPTIONS}>{itemText}</Markdown>
										{timestamp && (
											<span className={styles.contextTimestamp} onClick={handleItemTimestampClick}>
												{timestamp.replace(/^00:/, "")}
											</span>
										)}
									</div>
								</div>
							)}
							{processedContext}
						</Modal.Body>
					</Modal>
				)}
			</>
		);
	}

	return (
		<Popover.Root open={open} onOpenChange={handleOpenChange}>
			<Popover.Trigger asChild>
				{/* We need to wrap the children because otherwise tooltipped trigger will not work */}
				<div onClick={handleClick} style={{ display: "inline-block", cursor: "pointer" }}>
					{children}
				</div>
			</Popover.Trigger>
			<Popover.Portal>
				<Popover.Content className={styles.contextPopover} side="left" align="center" sideOffset={5}>
					<div className={styles.contextContent}>{processedContext}</div>
					<Popover.Arrow className={styles.contextPopoverArrow} />
				</Popover.Content>
			</Popover.Portal>
		</Popover.Root>
	);
};

// Helper function to process text segments (timestamps and bold text)
function processText(text: string, baseIndex: number, onTimestampClick: (timestamp: number) => void) {
	// Split on timestamps wrapped in backticks (HH:MM:SS or MM:SS) or bold text
	const segments = text.split(/(`\d{2}(?::\d{2}){1,2}`|\*{2}[^*]+\*{2})/g);

	return segments.map((segment, i) => {
		// Check if this segment is a timestamp
		const timestampMatch = segment.match(/^`(\d{2}):(\d{2}):(\d{2})`$/);
		if (timestampMatch) {
			const [, hours, minutes, seconds] = timestampMatch;
			const timestamp = parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseInt(seconds);

			// Only show hours if they're non-zero
			const displayText = parseInt(hours) > 0 ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;

			return (
				<span
					// eslint-disable-next-line react/no-array-index-key
					key={`${baseIndex}-${i}`}
					className={styles.contextTimestamp}
					onClick={e => {
						e.stopPropagation();
						onTimestampClick(timestamp);
					}}
				>
					{displayText}
				</span>
			);
		}

		// Check if this segment is bold text
		if (segment.startsWith("**") && segment.endsWith("**")) {
			// eslint-disable-next-line react/no-array-index-key
			return <strong key={`${baseIndex}-${i}`}>{segment.slice(2, -2)}</strong>;
		}

		return segment;
	});
}
